import { useRef, useCallback, useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay } from 'swiper/modules'
import FigmaIconFrame from '../common/FigmaIconFrame'
import { initiatives } from '../../data/homeContent'
import {
  HOME_MOBILE_MQ,
  homeMobilePaddingX,
  homeSectionPy,
  homeSectionTitleSx,
} from './homeMobileLayout'
import { SLIDER_TRANSITION_SPEED } from '../../theme/slider'
import 'swiper/css'

const SLIDE_GAP = 40
const MOBILE_SLIDE_GAP = 20
const SLIDE_WIDTH = 440
const CARD_SHADOW_BLEED = 8
const CARD_SHADOW = '0px 0px 8px 0px rgba(0,0,0,0.12)'
const CARD_SHADOW_HOVER = '0px 4px 20px 0px rgba(0,0,0,0.16)'

function KeyInitiativeCard({ title, description, iconSrc, iconBgColor, path }) {
  return (
    <Box
      component={path ? RouterLink : 'div'}
      to={path}
      sx={{
        width: '100%',
        height: '100%',
        minHeight: { xs: 'auto', lg: 381 },
        borderRadius: '20px',
        bgcolor: '#fffefa',
        boxShadow: CARD_SHADOW,
        p: '24px',
        boxSizing: 'border-box',
        textDecoration: 'none',
        color: '#111',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        overflow: 'hidden',
        transition: 'box-shadow 0.3s ease, transform 0.3s ease',
        ...(path && {
          '&:hover': {
            boxShadow: CARD_SHADOW_HOVER,
            transform: 'translateY(-2px)',
          },
        }),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: '44px', lg: '100px' },
          [HOME_MOBILE_MQ]: { gap: '44px' },
          width: '100%',
          flex: 1,
        }}
      >
        <FigmaIconFrame
          src={iconSrc}
          bgColor={iconBgColor}
          sx={{
            width: { xs: 64, lg: 80 },
            height: { xs: 64, lg: 80 },
            borderRadius: { xs: '8px', lg: '10px' },
            p: { xs: '8px', lg: '10px' },
            [HOME_MOBILE_MQ]: {
              width: 64,
              height: 64,
              borderRadius: '8px',
              p: '8px',
            },
            '& > div': {
              width: { xs: 38, lg: 48 },
              height: { xs: 38, lg: 48 },
              [HOME_MOBILE_MQ]: { width: 38, height: 38 },
            },
          }}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Typography
            sx={{
              fontSize: { xs: 22, lg: 22 },
              lineHeight: 'normal',
              fontWeight: 400,
              color: '#111',
              [HOME_MOBILE_MQ]: { fontSize: 22 },
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 14, lg: 20 },
              lineHeight: 'normal',
              fontWeight: 400,
              color: 'rgba(17,17,17,0.6)',
              [HOME_MOBILE_MQ]: { fontSize: 14 },
            }}
          >
            {description}
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

export default function KeyInitiativesSection() {
  const swiperRef = useRef(null)
  const slides = useMemo(() => [...initiatives, ...initiatives, ...initiatives], [])

  const handleSwiper = useCallback((swiper) => {
    swiperRef.current = swiper
    requestAnimationFrame(() => swiper.autoplay?.start())
  }, [])

  return (
    <Box
      component="section"
      sx={{
        width: '100%',
        maxWidth: 1920,
        mx: 'auto',
        pl: homeMobilePaddingX,
        pr: 0,
        py: homeSectionPy,
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: '32px', md: '80px' },
          [HOME_MOBILE_MQ]: { gap: '32px' },
        }}
      >
        <Box
          sx={{
            maxWidth: 1520,
            width: '100%',
            pr: homeMobilePaddingX,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            alignItems: 'flex-start',
            gap: { xs: '16px', md: 6, lg: '249px' },
            [HOME_MOBILE_MQ]: { gap: '16px' },
          }}
        >
          <Typography
            component="h2"
            sx={{
              ...homeSectionTitleSx,
              flexShrink: 0,
              whiteSpace: { xs: 'normal', lg: 'pre-line' },
              [HOME_MOBILE_MQ]: { fontSize: 28, whiteSpace: 'normal' },
            }}
          >
            {`Our Key\nInitiatives`}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 20, sm: 22, md: 28, lg: 36 },
              lineHeight: 'normal',
              fontWeight: 400,
              color: '#111',
              maxWidth: { lg: 1000 },
              width: '100%',
              [HOME_MOBILE_MQ]: { fontSize: 20 },
            }}
          >
            Comprehensive programs designed to develop leadership, cultural awareness, and social
            responsibility
          </Typography>
        </Box>

        <Box
          sx={{
            width: {
              xs: 'calc(100vw - 40px)',
              sm: 'calc(100vw - 48px)',
              md: 'calc(100vw - 80px)',
              lg: 'calc(100vw - 160px)',
              xl: 'calc(100vw - 400px)',
            },
            maxWidth: 'none',
            position: 'relative',
            overflow: 'visible',
            '& .swiper': { overflow: 'visible', width: '100%' },
            '& .swiper-wrapper': { alignItems: 'stretch' },
            '& .swiper-slide': {
              width: {
                xs: 'calc(100vw - 40px) !important',
                sm: '360px !important',
                md: '400px !important',
                lg: `${SLIDE_WIDTH + CARD_SHADOW_BLEED * 2}px !important`,
              },
              height: 'auto',
              flexShrink: 0,
              boxSizing: 'border-box',
              padding: `${CARD_SHADOW_BLEED}px`,
              overflow: 'visible',
              [HOME_MOBILE_MQ]: {
                width: 'calc(100vw - 40px) !important',
              },
            },
          }}
        >
          <Swiper
            modules={[Autoplay]}
            onSwiper={handleSwiper}
            slidesPerView="auto"
            spaceBetween={MOBILE_SLIDE_GAP}
            loop
            loopAdditionalSlides={initiatives.length}
            loopPreventsSliding={false}
            observer
            observeParents
            resizeObserver
            autoplay={{
              delay: 4000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
              waitForTransition: true,
            }}
            speed={SLIDER_TRANSITION_SPEED}
            grabCursor
            breakpoints={{
              426: { spaceBetween: 24 },
              900: { spaceBetween: 32 },
              1200: { spaceBetween: SLIDE_GAP },
            }}
          >
            {slides.map((item, index) => (
              <SwiperSlide key={`${item.title}-${index}`}>
                <KeyInitiativeCard {...item} />
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
      </Box>
    </Box>
  )
}
